import React, { useCallback, useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { AlertTriangle, RefreshCw, Download } from "react-feather";
import { toast } from "react-toastify";
import { getUsageOverviewAPI } from "../../../Services/mockApi";
import PageHeader from "../../../Components/shared/PageHeader";
import DataTableWrapper from "../../../Components/shared/DataTableWrapper";
import { platformUrl } from "../../../utils/mhNav";
import styles from "./UsageOverview.module.scss";

const METRICS = [
  { key: "meetings", label: "Meetings", icon: "📅" },
  { key: "ai_tokens", label: "AI Tokens", icon: "🤖" },
  { key: "storage", label: "Storage", icon: "💾" },
  { key: "channels", label: "Channels", icon: "📡" },
  { key: "users", label: "Users", icon: "👥" },
];

// ── Inline progress bar (no Bootstrap dependency) ────────────────────────────
function UsageBar({ pct }) {
  const color = pct >= 90 ? "#dc2626" : pct >= 70 ? "#f59e0b" : "#644998";
  return (
    <div className={styles.usageBarWrap}>
      <div className={styles.usageBarTrack}>
        <div
          className={styles.usageBarFill}
          style={{ width: `${Math.min(pct, 100)}%`, background: color }}
        />
      </div>
      <span className={styles.usageBarLabel} style={{ color }}>
        {pct}%
      </span>
    </div>
  );
}

// ── Status pill ───────────────────────────────────────────────────────────────
function StatusPill({ status }) {
  const map = {
    critical: { cls: styles.pillDanger, label: "Critical" },
    warning: { cls: styles.pillWarning, label: "Warning" },
    ok: { cls: styles.pillOk, label: "OK" },
  };
  const { cls, label } = map[status] || map.ok;
  return <span className={`${styles.pill} ${cls}`}>{label}</span>;
}

// ── Shared chart theme ────────────────────────────────────────────────────────
const chartTheme = {
  toolbar: { show: false },
  fontFamily: "inherit",
  background: "transparent",
};

export default function UsageOverview() {
  const [metric, setMetric] = useState("meetings");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getUsageOverviewAPI(metric, "last_90d");
      setData(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [metric]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Chart configs ──────────────────────────────────────────────────────────
  const stackOptions = useMemo(
    () => ({
      chart: { ...chartTheme, stacked: true },
      plotOptions: {
        bar: { horizontal: false, columnWidth: "52%", borderRadius: 4 },
      },
      xaxis: {
        categories: (data?.stack || []).map((r) => r.plan),
        labels: { style: { fontSize: "12px" } },
      },
      colors: ["#644998", "#e8e3f4"],
      legend: { position: "top", fontSize: "12px" },
      grid: { borderColor: "rgba(100,73,152,0.08)" },
      tooltip: { theme: "light" },
    }),
    [data],
  );

  const stackSeries = useMemo(() => {
    const s = data?.stack || [];
    return [
      { name: "Used", data: s.map((r) => r.used) },
      { name: "Headroom", data: s.map((r) => Math.max(0, r.limit - r.used)) },
    ];
  }, [data]);

  const hBarOptions = useMemo(
    () => ({
      chart: chartTheme,
      plotOptions: {
        bar: { horizontal: true, barHeight: "65%", borderRadius: 4 },
      },
      xaxis: {
        categories: (data?.topTenants || []).map((t) => t.tenantCode),
        labels: { style: { fontSize: "11px" } },
      },
      colors: ["#644998"],
      dataLabels: { enabled: false },
      grid: { borderColor: "rgba(100,73,152,0.08)" },
      tooltip: { theme: "light" },
    }),
    [data],
  );

  const hBarSeries = useMemo(
    () => [
      {
        name: "Consumption",
        data: (data?.topTenants || []).map((t) => t.value),
      },
    ],
    [data],
  );

  const lineOptions = useMemo(
    () => ({
      chart: chartTheme,
      xaxis: {
        categories: (data?.trend || []).map((t) => t.month),
        labels: { style: { fontSize: "12px" } },
      },
      stroke: { curve: "smooth", width: 2.5 },
      colors: ["#644998"],
      dataLabels: { enabled: false },
      fill: {
        type: "gradient",
        gradient: { shadeIntensity: 1, opacityFrom: 0.18, opacityTo: 0.02 },
      },
      grid: { borderColor: "rgba(100,73,152,0.08)" },
      markers: { size: 4, colors: ["#644998"], strokeWidth: 0 },
      tooltip: { theme: "light" },
    }),
    [data],
  );

  const lineSeries = useMemo(
    () => [{ name: "Usage", data: (data?.trend || []).map((t) => t.value) }],
    [data],
  );

  // ── Table columns ──────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        name: "Tenant",
        selector: (r) => r.tenant,
        sortable: true,
        wrap: true,
        cell: (r) => (
          <span style={{ fontWeight: 600, color: "#1a1525" }}>{r.tenant}</span>
        ),
      },
      {
        name: "Used",
        selector: (r) => r.metricUsed,
        sortable: true,
        width: "110px",
        cell: (r) => (
          <span style={{ fontWeight: 600, color: "#644998" }}>
            {typeof r.metricUsed === "number"
              ? r.metricUsed.toLocaleString()
              : r.metricUsed}
          </span>
        ),
      },
      {
        name: "Limit",
        selector: (r) => r.limit,
        sortable: true,
        width: "90px",
      },
      {
        name: "% Used",
        selector: (r) => r.pctUsed,
        width: "180px",
        cell: (r) => <UsageBar pct={r.pctUsed} />,
      },
      {
        name: "Forecast",
        selector: (r) => r.forecast,
        cell: (r) => (
          <span>
            {typeof r.forecast === "number"
              ? r.forecast.toLocaleString()
              : r.forecast}
            {r.forecastExceeds && (
              <span className={styles.forecastWarn}> ⚠ Exceeds</span>
            )}
          </span>
        ),
      },
      {
        name: "Status",
        selector: (r) => r.status,
        width: "110px",
        cell: (r) => <StatusPill status={r.status} />,
      },
    ],
    [],
  );

  const rowStyles = useMemo(
    () => [
      {
        when: (r) => r.status === "critical",
        style: { backgroundColor: "rgba(220,38,38,0.04)" },
      },
      {
        when: (r) => r.status === "warning",
        style: { backgroundColor: "rgba(245,158,11,0.05)" },
      },
    ],
    [],
  );

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading && !data) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Usage Overview"
          breadcrumbs={[
            { label: "Platform", path: platformUrl("dashboard") },
            { label: "Usage" },
          ]}
        />
        <div className={styles.skeletonGrid}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={styles.skeletonCard}>
              {[1, 2, 3, 4].map((j) => (
                <div key={j} className={styles.skeletonRow} />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Usage Overview"
          breadcrumbs={[
            { label: "Platform", path: platformUrl("dashboard") },
            { label: "Usage" },
          ]}
        />
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <AlertTriangle size={22} />
          </div>
          <h3>Could not load usage data</h3>
          <p>{error.message}</p>
          <button className={styles.btnPrimary} onClick={load}>
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      </div>
    );
  }

  // ── Main ───────────────────────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      <PageHeader
        title="Usage Overview"
        breadcrumbs={[
          { label: "Platform", path: platformUrl("dashboard") },
          { label: "Usage" },
        ]}
        secondaryActions={[
          {
            label: "Export",
            onClick: () => toast.success("Export started (simulated)"),
          },
        ]}
      />

      {/* ── Metric pill nav ── */}
      <div className={styles.metricNav}>
        {METRICS.map((m) => (
          <button
            key={m.key}
            className={`${styles.metricPill} ${metric === m.key ? styles.metricPillActive : ""}`}
            onClick={() => setMetric(m.key)}
          >
            <span className={styles.metricIcon}>{m.icon}</span>
            {m.label}
          </button>
        ))}
        <button
          className={styles.exportBtn}
          onClick={() => toast.success("Export started (simulated)")}
        >
          <Download size={14} /> Export
        </button>
      </div>

      {/* ── Loading overlay shimmer when switching metrics ── */}
      {loading && (
        <div className={styles.metricLoading}>
          <div className={styles.loadingBar} />
        </div>
      )}

      {/* ── Charts row ── */}
      <div className={styles.chartsRow}>
        <div className={styles.chartCard} style={{ flex: 2 }}>
          <div className={styles.chartCardHeader}>
            <h6>Usage by plan</h6>
            <span className={styles.chartSub}>Stacked used vs headroom</span>
          </div>
          {data?.stack?.length > 0 && (
            <Chart
              options={stackOptions}
              series={stackSeries}
              type="bar"
              height={300}
            />
          )}
        </div>

        <div className={styles.chartCard} style={{ flex: 1 }}>
          <div className={styles.chartCardHeader}>
            <h6>Top 10 tenants</h6>
            <span className={styles.chartSub}>By consumption</span>
          </div>
          {data?.topTenants?.length > 0 && (
            <Chart
              options={hBarOptions}
              series={hBarSeries}
              type="bar"
              height={300}
            />
          )}
        </div>
      </div>

      {/* ── Trend line ── */}
      <div className={styles.chartCard} style={{ marginBottom: "1.5rem" }}>
        <div className={styles.chartCardHeader}>
          <h6>6-month trend</h6>
          <span className={styles.chartSub}>Monthly usage volume</span>
        </div>
        {data?.trend?.length > 0 && (
          <Chart
            options={lineOptions}
            series={lineSeries}
            type="area"
            height={240}
          />
        )}
      </div>

      {/* ── Tenants table ── */}
      <div className={styles.tableCard}>
        <div className={styles.tableCardHeader}>
          <h6>Tenant breakdown</h6>
          <span className={styles.tableCount}>
            {(data?.tableRows || []).length} tenants
          </span>
        </div>
        <DataTableWrapper
          columns={columns}
          data={data?.tableRows || []}
          loading={false}
          customTableProps={{ conditionalRowStyles: rowStyles }}
        />
      </div>
    </div>
  );
}
